/**
 * Source-supplied name classification, shared by the result pages and the
 * detail panel. The backend sends `details.name_classifications` and
 * `details.matched_name_type`; nothing here invents a classification — a name
 * the source did not type is shown as the neutral "Matched name".
 */
export const NEUTRAL_NAME_TYPE = 'Matched name';

const TYPE_ORDER = ['Primary name', 'AKA', 'Weak AKA', 'Former name', 'Known name', NEUTRAL_NAME_TYPE];

export const NAME_TYPE_STYLES = {
  'Primary name': 'bg-slate-900 text-white border-slate-900',
  AKA: 'bg-amber-100 text-amber-800 border-amber-200',
  'Weak AKA': 'bg-slate-100 text-slate-600 border-slate-300',
  'Former name': 'bg-sky-100 text-sky-800 border-sky-200',
  'Known name': 'bg-emerald-50 text-emerald-800 border-emerald-200',
  [NEUTRAL_NAME_TYPE]: 'bg-white text-slate-600 border-slate-300',
};

export const nameTypeClass = (type) =>
  NAME_TYPE_STYLES[type] || NAME_TYPE_STYLES[NEUTRAL_NAME_TYPE];

export const matchedNameType = (match) =>
  match?.details?.matched_name_type || NEUTRAL_NAME_TYPE;

/** Classifications grouped by type, in reviewer-meaningful order. */
export const groupNameClassifications = (classifications = []) => {
  const byType = new Map();
  classifications.forEach(({ name, type }) => {
    const label = type || NEUTRAL_NAME_TYPE;
    if (!byType.has(label)) byType.set(label, []);
    byType.get(label).push(name);
  });
  return [...byType.entries()]
    .sort((a, b) => {
      const ai = TYPE_ORDER.indexOf(a[0]);
      const bi = TYPE_ORDER.indexOf(b[0]);
      return (ai === -1 ? TYPE_ORDER.length : ai) - (bi === -1 ? TYPE_ORDER.length : bi);
    })
    .map(([type, names]) => ({ type, names }));
};

/** The listing ID and authority that produced a match. */
export const listingIdentity = (match) => ({
  recordId: match?.details?.source_record_id || match?.details?.entity_id || match?.entity_id || null,
  authority: match?.details?.source_authority || match?.source || null,
});

export const sameListing = (a, b) => {
  const ia = listingIdentity(a);
  const ib = listingIdentity(b);
  return ia.recordId === ib.recordId && ia.authority === ib.authority;
};
